"use client";

import React, { createContext, useContext, ReactNode, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import { Worker } from '@/app/components/workercard/WorkerCard';
import { api, Service, Order } from '@/lib/api';



interface OrdersContextType {
    // Orders (My Team)
    orders: Order[];
    loadingOrders: boolean;
    fetchOrders: (role?: 'buyer' | 'seller', workspaceId?: string, options?: { force?: boolean }) => Promise<void>;
    workersFromOrders: Worker[];
    loadingOrderWorkers: boolean;

    // Marketplace (Services)
    marketplaceWorkers: Worker[];
    loadingMarketplace: boolean;
    fetchMarketplace: (workspaceId?: string, options?: { force?: boolean }) => Promise<void>;

    // Combined loading state
    isInitialLoading: boolean;
}

const OrdersContext = createContext<OrdersContextType | undefined>(undefined);

// Global cache to prevent refetching on re-mounts
let cachedServices: Service[] = [];
let cachedOrders: Order[] = [];
let cachedMarketplaceWorkers: Worker[] = [];
let cachedMyTeamWorkers: Worker[] = [];
let servicesLastFetchTime: number = 0;
let servicesCacheKey: string = '';
let ordersLastFetchTime: number = 0;
let ordersCacheKey: string = '';
let servicesInflight: Promise<Service[]> | null = null;
let servicesInflightKey = '';
let ordersInflight: Promise<Order[]> | null = null;
let ordersInflightKey = '';
const CACHE_DURATION_MS = 60000; // 1 minute cache

function getOrdersCacheKey(role?: 'buyer' | 'seller', workspaceId?: string) {
    return `${role || 'all'}:${workspaceId || 'all-workspaces'}`;
}

/**
 * Clears all global caches and reset fetch timestamps.
 * Useful for logout scenarios to ensure the next user doesn't see old data.
 */
export function clearOrdersCache() {
    cachedServices = [];
    cachedOrders = [];
    cachedMarketplaceWorkers = [];
    cachedMyTeamWorkers = [];
    servicesLastFetchTime = 0;
    servicesCacheKey = '';
    ordersLastFetchTime = 0;
    ordersCacheKey = '';
    servicesInflight = null;
    servicesInflightKey = '';
    ordersInflight = null;
    ordersInflightKey = '';
}

// Helper to transform service to Worker format
function transformServiceToWorker(service: Service, order?: Order): Worker {
    const firstPackage = service.packages?.[0];
    const creatorId = service.userId || service.creatorId || service.serviceCreatorId;

    return {
        id: service.id || '',
        orderId: order?.id,
        name: order?.taskTitle || service.title || "Unnamed Service",
        role: order?.taskDescription?.substring(0, 50) || service.description?.substring(0, 50) || "AI Worker",
        archetype: order ? "Hired Worker" : "API Service",
        rating: service.averageRating || 0,
        hires: service.completedPurchasesCount || 0,
        retainer: order?.amount ?? firstPackage?.price ?? 0,
        wage: 0,
        imageColor: "bg-indigo-100 text-indigo-600",
        skills: service.tags?.map(tag => tag.value) || [],
        tone: "Professional and efficient",
        description: service.description || "AI-powered service",
        completedPurchasesCount: service.completedPurchasesCount || 0,
        creatorId: creatorId,
        thumbnail: service.thumbnail,
        orderStatus: order?.status,
        workspaceId: order?.workspaceId || (order?.workspace as any)?.id // Extract workspaceId
    };
}

export function OrdersProvider({ children }: { children: ReactNode }) {
    // Services state (primary data source for both sections)
    const [services, setServices] = useState<Service[]>(cachedServices);
    const [loadingServices, setLoadingServices] = useState(cachedServices.length === 0);

    // Orders state
    const [orders, setOrders] = useState<Order[]>(cachedOrders);
    const [loadingOrders, setLoadingOrders] = useState(cachedOrders.length === 0);

    // Derived workers
    const [marketplaceWorkers, setMarketplaceWorkers] = useState<Worker[]>(cachedMarketplaceWorkers);
    const [workersFromOrders, setWorkersFromOrders] = useState<Worker[]>(cachedMyTeamWorkers);

    const fetchStartedRef = useRef(false);
    const searchParams = useSearchParams();
    const initialWorkspaceId = searchParams?.get('workspaceId') || undefined;

    // Fetch services - this is FAST and provides data for BOTH sections
    const fetchServices = useCallback(async (forceRefresh = false, workspaceId?: string) => {
        const now = Date.now();
        const inflightKey = workspaceId || 'all';
        if (!forceRefresh && servicesCacheKey === inflightKey && cachedServices.length > 0 && (now - servicesLastFetchTime) < CACHE_DURATION_MS) {
            setServices(cachedServices);
            setMarketplaceWorkers(cachedMarketplaceWorkers);
            setLoadingServices(false);
            return cachedServices;
        }
        if (!forceRefresh && servicesInflight && servicesInflightKey === inflightKey) {
            return servicesInflight;
        }

        const run = (async (): Promise<Service[]> => {
        try {
            setLoadingServices(true);
            const servicesResponse = await api.services.list({
                isActive: "true",
                workspaceId: workspaceId || undefined
            });

            let serviceList: Service[] = [];
            if (Array.isArray(servicesResponse)) {
                serviceList = servicesResponse;
            } else if (servicesResponse && typeof servicesResponse === 'object' && 'services' in servicesResponse) {
                const servicesData = (servicesResponse as { services?: Service[] }).services;
                serviceList = Array.isArray(servicesData) ? servicesData : [];
            }

            // Update cache and state
            cachedServices = serviceList;
            servicesLastFetchTime = now;
            servicesCacheKey = inflightKey;
            setServices(serviceList);

            // Transform to workers for Marketplace
            const workers = serviceList.map(s => transformServiceToWorker(s));
            cachedMarketplaceWorkers = workers;
            setMarketplaceWorkers(workers);

            return serviceList;
        } catch (error) {
            // Avoid `console.error` in dev — Next.js can surface it as a blocking overlay.
            console.warn("Failed to fetch services:", error);
            return cachedServices;
        } finally {
            setLoadingServices(false);
        }
        })();

        servicesInflight = run;
        servicesInflightKey = inflightKey;
        try {
            return await run;
        } finally {
            if (servicesInflight === run) {
                servicesInflight = null;
                servicesInflightKey = '';
            }
        }
    }, []);

    // Fetch orders - this is SLOW so we do it in background
    const fetchOrdersOnly = useCallback(async (role?: 'buyer' | 'seller', forceRefresh = false, workspaceId?: string) => {
        const now = Date.now();
        const cacheKey = getOrdersCacheKey(role, workspaceId);
        if (!forceRefresh && ordersCacheKey === cacheKey && cachedOrders.length > 0 && (now - ordersLastFetchTime) < CACHE_DURATION_MS) {
            setOrders(cachedOrders);
            return cachedOrders;
        }
        if (!forceRefresh && ordersInflight && ordersInflightKey === cacheKey) {
            return ordersInflight;
        }

        if (ordersCacheKey !== cacheKey) {
            setOrders([]);
            setWorkersFromOrders([]);
        }

        if (typeof window !== 'undefined' && !localStorage.getItem('auth_token')?.trim()) {
            setLoadingOrders(false);
            cachedOrders = [];
            ordersLastFetchTime = 0;
            ordersCacheKey = '';
            setOrders([]);
            return [];
        }

        const run = (async (): Promise<Order[]> => {
        try {
            setLoadingOrders(true);
            const response = await api.orders.list({
                role,
                workspaceId: workspaceId || undefined
            });

            let orderList: Order[] = [];
            if (Array.isArray(response)) {
                orderList = response;
            } else if (response && typeof response === 'object' && 'orders' in response) {
                const ordersData = (response as { orders?: Order[] }).orders;
                orderList = Array.isArray(ordersData) ? ordersData : [];
            }

            cachedOrders = orderList;
            ordersLastFetchTime = now;
            ordersCacheKey = cacheKey;
            setOrders(orderList);

            return orderList;
        } catch (error) {
            console.warn('Error fetching orders:', error);
            return cachedOrders;
        } finally {
            setLoadingOrders(false);
        }
        })();

        ordersInflight = run;
        ordersInflightKey = cacheKey;
        try {
            return await run;
        } finally {
            if (ordersInflight === run) {
                ordersInflight = null;
                ordersInflightKey = '';
            }
        }
    }, []);

    // Build My Team workers from services + orders
    const buildMyTeamWorkers = useCallback((serviceList: Service[], orderList: Order[]) => {
        if (orderList.length === 0) {
            cachedMyTeamWorkers = [];
            setWorkersFromOrders([]);
            return;
        }

        // Deduplicate by workspace + serviceId so My Team shows unique purchased services per workspace.
        // Keep the most recently updated order when duplicates exist.
        const latestOrderByServiceId = new Map<string, Order>();
        for (const order of orderList) {
            const orderWorkspaceId = order.workspaceId || (order.workspace as any)?.id || '';
            const orderKey = `${orderWorkspaceId}:${order.serviceId}`;
            const prev = latestOrderByServiceId.get(orderKey);
            if (!prev) {
                latestOrderByServiceId.set(orderKey, order);
                continue;
            }
            const prevTs = Date.parse(prev.updatedAt || prev.createdAt || '');
            const nextTs = Date.parse(order.updatedAt || order.createdAt || '');
            if ((Number.isFinite(nextTs) ? nextTs : 0) >= (Number.isFinite(prevTs) ? prevTs : 0)) {
                latestOrderByServiceId.set(orderKey, order);
            }
        }

        const dedupedOrders = Array.from(latestOrderByServiceId.values());

        const workers: Worker[] = dedupedOrders.map((order) => {
            const matchingService = serviceList.find(s => s.id === order.serviceId);

            if (matchingService) {
                return transformServiceToWorker(matchingService, order);
            } else {
                // Fallback if service not found
                return {
                    id: order.serviceId,
                    orderId: order.id,
                    name: order.taskTitle || 'AI Worker',
                    role: order.taskDescription?.substring(0, 50) || 'AI Worker',
                    archetype: 'Hired Worker',
                    rating: 0,
                    hires: 0,
                    retainer: order.amount || 0,
                    wage: 0,
                    imageColor: 'bg-gray-100 text-gray-600',
                    skills: [],
                    tone: 'Professional',
                    description: order.taskDescription || 'Hired service',
                    orderStatus: order.status,
                    workspaceId: order.workspaceId || (order.workspace as any)?.id
                } as Worker;
            }
        });

        cachedMyTeamWorkers = workers;
        setWorkersFromOrders(workers);
    }, []);

    // Combined fetch - fetch both in parallel
    const fetchAll = useCallback(async (forceRefresh = false, workspaceId?: string) => {
        // Start both fetches in parallel
        const [serviceList, orderList] = await Promise.all([
            fetchServices(forceRefresh, workspaceId),
            fetchOrdersOnly('buyer', forceRefresh, workspaceId)
        ]);

        // Build My Team workers from combined data
        buildMyTeamWorkers(serviceList, orderList);
    }, [fetchServices, fetchOrdersOnly, buildMyTeamWorkers]);

    // Wrapper for external use
    const fetchOrders = useCallback(async (role?: 'buyer' | 'seller', workspaceId?: string, options?: { force?: boolean }) => {
        const orderList = await fetchOrdersOnly(role, options?.force ?? true, workspaceId);
        buildMyTeamWorkers(services, orderList);
    }, [fetchOrdersOnly, buildMyTeamWorkers, services]);

    const fetchMarketplace = useCallback(async (workspaceId?: string, options?: { force?: boolean }) => {
        await fetchServices(options?.force ?? true, workspaceId);
    }, [fetchServices]);

    const pathname = usePathname();

    useEffect(() => {
        // Skip fetching on public auth pages to prevent 401 loops / wasted calls in LIFF.
        const isAuthPage =
            pathname?.startsWith('/login') ||
            pathname?.startsWith('/signin') ||
            pathname?.startsWith('/register') ||
            pathname?.startsWith('/auth') ||
            pathname?.startsWith('/s/');

        if (!fetchStartedRef.current && !isAuthPage) {
            fetchStartedRef.current = true;
            // Use the initial workspaceId from URL if available
            void fetchAll(false, initialWorkspaceId).catch((err) => {
                console.warn('[OrdersProvider] initial fetchAll failed:', err);
            });
        }
    }, [fetchAll, initialWorkspaceId, pathname]);

    // After login (same tab), refetch — initial load may have skipped orders without a token
    useEffect(() => {
        const onLoginSuccess = () => {
            if (!localStorage.getItem('auth_token')?.trim()) return;
            void fetchAll(true, initialWorkspaceId).catch((err) => {
                console.warn('[OrdersProvider] fetchAll after login failed:', err);
            });
        };
        window.addEventListener('auth:loginSuccess', onLoginSuccess);
        return () => window.removeEventListener('auth:loginSuccess', onLoginSuccess);
    }, [fetchAll, initialWorkspaceId]);

    // Handle logout event
    useEffect(() => {
        const handleLogout = () => {

            clearOrdersCache();

            // Reset local states
            setServices([]);
            setOrders([]);
            setMarketplaceWorkers([]);
            setWorkersFromOrders([]);

            // Reset loading states so next mount/login triggers fresh fetch
            setLoadingServices(true);
            setLoadingOrders(true);

            // Reset fetch ref so if provider stays mounted, it can fetch again on next login
            fetchStartedRef.current = false;
        };

        window.addEventListener("auth:logout", handleLogout);
        return () => {
            window.removeEventListener("auth:logout", handleLogout);
        };
    }, []);

    // Update My Team when services or orders change
    useEffect(() => {
        buildMyTeamWorkers(services, orders);
    }, [services, orders, buildMyTeamWorkers]);

    // Combined loading state - only true when both have no cached data
    const loadingOrderWorkers = loadingServices || loadingOrders;
    const loadingMarketplace = loadingServices;

    const isInitialLoading = useMemo(() => {
        const hasNoCachedData = workersFromOrders.length === 0 && marketplaceWorkers.length === 0;
        return (loadingServices || loadingOrders) && hasNoCachedData;
    }, [loadingServices, loadingOrders, workersFromOrders.length, marketplaceWorkers.length]);

    const value = {
        orders,
        loadingOrders,
        fetchOrders,
        workersFromOrders,
        loadingOrderWorkers,
        marketplaceWorkers,
        loadingMarketplace,
        fetchMarketplace,
        isInitialLoading,
    };

    return (
        <OrdersContext.Provider value={value}>
            {children}
        </OrdersContext.Provider>
    );
}

export function useOrdersContext() {
    const context = useContext(OrdersContext);
    if (context === undefined) {
        throw new Error('useOrdersContext must be used within an OrdersProvider');
    }
    return context;
}
